import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { useEffect, useMemo, useRef } from 'react';
import FileTable from '/@/components/file-table';
import useDragAndDrop from '/@/hooks/drag-and-drop/useDragAndDrop';
import { _ } from '/@/locale';
import Alert from '../bayer/alert';

const getExtension = (file) => `.${file.name.split('.').pop()}`;
const requiredExtensionsToString = (requiredExtensions) =>
    '(' + requiredExtensions.map((el) => el.join(', ')).join(_(') o (')) + ')';

// requiredExtensions = [[this AND this AND this] OR [this AND this ...] ...]
function filterValidFiles(files, requiredExtensions = []) {
    const errors = [];
    const extensionsMatched = requiredExtensions.map((eGroup) =>
        eGroup.reduce((pv, cv) => {
            pv[cv] = false;
            return pv;
        }, {})
    );

    // Check each file against the required extensions
    for (const file of files) {
        const extension = getExtension(file);
        extensionsMatched.forEach((extensionsGroup) => {
            if (extensionsGroup[extension] === false) {
                extensionsGroup[extension] = true;
            }
        });
    }

    // All files match at least one required extension group
    for (const extensionsGroup of extensionsMatched) {
        if (Object.values(extensionsGroup).every(Boolean))
            return files.filter((file) => {
                const extension = getExtension(file);
                return extensionsGroup[extension];
            });
    }
    return [];
}

// Si llega a ser necesario
// TODO: nueva prop validExtentions: lista alternativa de extensiones validas a requiredExtentions para que no sean requeridas
// TODO: nueva prop sameName: para que los nombres sean iguales
// TODO: Cambiar textos por props
const FileDropper = ({
    requiredExtensions = [],
    files,
    setFiles,
    onValidFiles = () => {},
    title,
    buttonLabel,
    description,
}: {
    requiredExtensions: string[][];
    files: File[];
    setFiles: React.Dispatch<File[]>;
    onValidFiles?: (validFiles: File[]) => void;
    // Optional copy overrides. Defaults below keep the generic, extension-derived text used
    // by the field-upload screen; the N-Smart flow passes its own `description`.
    title?: string;
    buttonLabel?: string;
    description?: string;
}) => {
    const { dragOver, setDragOver, onDragOver, onDragLeave, fileDropError, setFileDropError } = useDragAndDrop();

    const fileInput = useRef();

    const dropTitle = title ?? _('Arrastrá un archivo acá o elegí uno de tu equipo');
    const selectLabel = buttonLabel ?? _('Seleccionar archivo');
    const helperText =
        description ??
        _('Deben cargarse archivos con extensiones: {{extentions}} con idéntico nombre.', {
            extentions: requiredExtensionsToString(requiredExtensions),
        });

    const validateFiles = (selectedFiles) => {
        const errors = [];
        try {
            const loadedFilesExtensions = {};
            let name;

            // Check loaded files
            for (const file of files) {
                if (!name) name = file.name.slice(0, file.name.length - 4);
                const extention = getExtension(file);
                loadedFilesExtensions[extention] = true;
            }

            // Check new files
            for (const file of Object.values(selectedFiles)) {
                const extention = getExtension(file);
                if (!name) name = file.name.slice(0, file.name.length - 4);
                // Check invalid extension
                if (!requiredExtensions.some((extensionsGroup) => extensionsGroup.includes(extention))) {
                    errors.push(
                        _(
                            'Archivo: {{file}}: extension incorrecta, el archivo debe poseer extension: {{extentions}}.',
                            {
                                extentions: requiredExtensionsToString(requiredExtensions),
                                file: file.name,
                            }
                        )
                    );
                    continue;
                }

                // Check same extension
                if (loadedFilesExtensions[extention]) {
                    errors.push(
                        _('Archivo: {{file}}: cargaste mas de un archivo con extension {{extention}}.', {
                            extention: extention,
                            file: file.name,
                        })
                    );
                    continue;
                }
                loadedFilesExtensions[extention] = true;

                // Check name
                if (file.name.slice(0, file.name.length - 4) !== name) {
                    errors.push(
                        _('Archivo: {{file}}: el archivo posee el nombre diferente a {{name}}.', {
                            name,
                            file: file.name,
                        })
                    );
                    continue;
                }

                setFiles((files) => [...files, file]);
            }

            // Load files
            setFileDropError(errors);
        } catch (error) {
            setFileDropError([_('Error inesperado...')]);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();

        setDragOver(false);

        const selectedFiles = e.dataTransfer.files;
        validateFiles(selectedFiles);
    };

    const onFilesSelected = (e) => {
        const selectedFiles = e.target.files;

        validateFiles(selectedFiles);
    };

    const onDelete = (file) => {
        setFileDropError([]);
        setFiles((files) => files.filter((el) => el.name !== file.name));
    };

    const validFiles = useMemo(() => {
        return filterValidFiles(files, requiredExtensions);
    }, [files, requiredExtensions]);
    useEffect(() => onValidFiles(validFiles), [validFiles]);

    return (
        <Grid
            item
            sx={{ width: '100%' }}
            direction='column'
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {validFiles.length === 0 && (
                <Stack
                    alignItems='center'
                    justifyContent='center'
                    spacing={3}
                    sx={{
                        border: '1px dashed rgba(16, 56, 79, 0.12)',
                        backgroundColor: dragOver ? '#006a281a' : '#F5F5F5',
                        p: 2,
                        borderRadius: '8px',
                    }}
                >
                    <input
                        type='file'
                        name='file'
                        ref={fileInput}
                        multiple
                        hidden
                        accept={requiredExtensions.map((el) => el.join(',')).join(',')}
                        onChange={onFilesSelected}
                    />

                    <Typography variant='body2' color='primary' sx={{ textAlign: 'center' }}>
                        {dropTitle}
                    </Typography>

                    <Button
                        variant='outlined'
                        endIcon={<AttachFileIcon />}
                        onClick={() => fileInput.current?.click()}
                        data-testid='file-dropper-button-select-files'
                        sx={{
                            backgroundColor: 'white !important',
                            borderColor: 'rgba(16, 56, 79, 0.12)',
                            visibility: dragOver ? 'hidden' : 'visible',
                        }}
                    >
                        {selectLabel}
                    </Button>

                    <Typography variant='caption' sx={{ color: 'rgba(16, 56, 79, 0.73)', textAlign: 'center' }}>
                        {helperText}
                    </Typography>

                    {fileDropError.map((err) => (
                        <Alert severity='danger'>{err}</Alert>
                    ))}
                </Stack>
            )}

            {files.length > 0 && <FileTable files={files} onDelete={onDelete} />}
        </Grid>
    );
};

export default FileDropper;
